import { computed, makeObservable } from 'mobx';

import { concatPath, toQueryString } from '@feathr/hooks';
import type { IBaseAttributes, TConstraints } from '@feathr/rachis';
import { Collection, Model } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';

import type {
  EOnboardingMilestone,
  EOnboardingTaskType,
  EOnboardingTaskTypeClass,
} from './onboarding_tasks.types';
import { EOnboardingTaskState } from './onboarding_tasks.types';

/**
 * Milestone = A grouping of tasks. Milestones are serialized pseudo-documents
 * created when the request is made. They cannot be filtered on nor fetched
 * individually because they are not persisted documents in Mongo or Elasticsearch.
 */
export interface IOnboardingMilestone extends IBaseAttributes {
  id: string;
  type: 'OnboardingMilestone';
  milestone: EOnboardingMilestone;
  total_time: number;
  time_completed: number;
  time_remaining: number;
  state: EOnboardingTaskState;
}

/**
 * Task = A discrete objective to be completed.
 * Tasks are updated by the backend when we fetch them.
 */
export interface IOnboardingTask extends IBaseAttributes {
  _cls: EOnboardingTaskTypeClass;
  account: string;
  milestone: EOnboardingMilestone;
  state: EOnboardingTaskState;
  optional: boolean;

  /** Serialized field, cannot be filtered on */
  type: EOnboardingTaskType;
  /** Serialized field, cannot be filtered on */
  minutes_to_complete: number;

  user_can_edit: boolean;
  user_can_clone: boolean;
}

export interface IOnboardingMilestonesProgress {
  completed: number;
  total: number;
}

export class OnboardingTask extends Model<IOnboardingTask> {
  public readonly className = 'OnboardingTask';
  public override collection: OnboardingTasks<this> | null = null;

  public get constraints(): TConstraints<IOnboardingTask> {
    return {};
  }

  constructor(attributes: Partial<IOnboardingTask> = {}) {
    super(attributes);
    makeObservable(this);
  }

  @computed
  public get isCompleted(): boolean {
    return this.get('state') === EOnboardingTaskState.Completed;
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath(`/onboarding_tasks/${this.id}`, pathSuffix);
  }
}

export class OnboardingTasks<
  Model extends OnboardingTask = OnboardingTask,
> extends Collection<Model> {
  public getClassName(): string {
    return 'onboarding_tasks';
  }

  public getModel(attributes: Partial<IOnboardingTask>): Model {
    return new OnboardingTask(attributes) as Model;
  }

  public async getMilestones(): Promise<IOnboardingMilestone[]> {
    const url = `${this.url()}milestones`;
    const response = await wretch<IOnboardingMilestone[]>(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public async getMilestonesProgress(): Promise<IOnboardingMilestonesProgress> {
    const milestones = await this.getMilestones();
    const completedMilestones = milestones.filter(
      (m) => m.state === EOnboardingTaskState.Completed,
    );
    return {
      completed: completedMilestones.length,
      total: milestones.length,
    };
  }

  public async getMilestone(milestone: EOnboardingMilestone): Promise<IOnboardingMilestone> {
    const response = await this.getMilestones();
    const match = response.find((m) => m.milestone === milestone);
    if (!match) {
      throw new Error(`Milestone ${milestone} not found`);
    }

    return match;
  }

  public async getMilestoneTasks(milestone: EOnboardingMilestone): Promise<IOnboardingTask[]>;
  public async getMilestoneTasks(milestones: EOnboardingMilestone[]): Promise<IOnboardingTask[]>;
  public async getMilestoneTasks(
    milestoneOrMilestones: EOnboardingMilestone | EOnboardingMilestone[],
  ): Promise<IOnboardingTask[]> {
    const milestones = Array.isArray(milestoneOrMilestones)
      ? milestoneOrMilestones
      : [milestoneOrMilestones];
    const query = toQueryString(this.parseJSONParams({ filters: { milestones__in: milestones } }));
    const url = `${this.url()}?${query}`;

    const response = await wretch<IOnboardingTask[]>(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public async updateMilestoneTask(
    taskId: string,
    state: EOnboardingTaskState.InProgress | EOnboardingTaskState.Skipped,
  ): Promise<IOnboardingTask> {
    const url = `${this.url()}${taskId}`;
    const response = await wretch<IOnboardingTask>(url, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify({ state }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }
}
